import { getSettings, onSettingsChange } from '../services/settings'
import type { Settings } from '../types'
import type { GameAudio } from '../game/GameScene'

/** Fully synthesized Web Audio: SFX + looping ambient pad. No external assets. */
export class AudioManager implements GameAudio {
  private ctx: AudioContext | null = null
  private master!: GainNode
  private musicBus!: GainNode
  private sfxBus!: GainNode
  private ambientNodes: AudioNode[] = []
  private ambientTimer: number | null = null
  private unlocked = false
  /** Pre-rendered noise burst for the capture squirt — built once, replayed via a fresh
   * BufferSource each call so rapid chain-captures don't refill ~11k samples per hit. */
  private squirtNoise: AudioBuffer | null = null

  constructor() {
    onSettingsChange((s) => this.applyVolumes(s))
  }

  get state(): string {
    return this.ctx?.state ?? 'none'
  }

  /** Must be called from a user gesture (click/keydown) to satisfy autoplay policy. */
  unlock(): void {
    if (this.unlocked) {
      void this.ctx?.resume()
      return
    }
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.ctx = new Ctor()
    this.master = this.ctx.createGain()
    this.musicBus = this.ctx.createGain()
    this.sfxBus = this.ctx.createGain()
    this.musicBus.connect(this.master)
    this.sfxBus.connect(this.master)
    this.master.connect(this.ctx.destination)
    this.applyVolumes(getSettings())
    this.unlocked = true
    void this.ctx.resume()
  }

  private applyVolumes(s: Settings): void {
    if (!this.ctx) return
    const now = this.ctx.currentTime
    this.master.gain.setTargetAtTime(s.master, now, 0.05)
    this.musicBus.gain.setTargetAtTime(s.music, now, 0.05)
    this.sfxBus.gain.setTargetAtTime(s.sfx, now, 0.05)
  }

  // --------------------------------------------------------------------------
  // SFX
  // --------------------------------------------------------------------------
  play(name: string): void {
    if (!this.ctx) return
    switch (name) {
      case 'pearl':
        return this.blip(880 + Math.random() * 120, 0.07, 'triangle', 0.18)
      case 'shell':
        this.blip(660, 0.12, 'sine', 0.22)
        this.blip(990, 0.14, 'sine', 0.16, 0.05)
        return
      case 'power':
        return this.sweep(220, 880, 0.5, 0.3)
      case 'capture':
        return this.squirt()
      case 'death':
        return this.sweep(660, 110, 0.7, 0.3, 'sawtooth')
      case 'levelclear':
        ;[523, 659, 784, 1046].forEach((f, i) => this.blip(f, 0.18, 'triangle', 0.2, i * 0.1))
        return
      case 'click':
        return this.blip(440, 0.05, 'square', 0.12)
      case 'select':
        return this.blip(620, 0.08, 'sine', 0.16)
      default:
        return
    }
  }

  private blip(freq: number, dur: number, type: OscillatorType, gain: number, delay = 0): void {
    if (!this.ctx) return
    const t0 = this.ctx.currentTime + delay
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(freq, t0)
    g.gain.setValueAtTime(0, t0)
    g.gain.linearRampToValueAtTime(gain, t0 + 0.008)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    osc.connect(g)
    g.connect(this.sfxBus)
    osc.start(t0)
    osc.stop(t0 + dur + 0.02)
    osc.onended = () => {
      osc.disconnect()
      g.disconnect()
    }
  }

  private sweep(from: number, to: number, dur: number, gain: number, type: OscillatorType = 'sine'): void {
    if (!this.ctx) return
    const t0 = this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(from, t0)
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), t0 + dur)
    g.gain.setValueAtTime(0, t0)
    g.gain.linearRampToValueAtTime(gain, t0 + 0.02)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    osc.connect(g)
    g.connect(this.sfxBus)
    osc.start(t0)
    osc.stop(t0 + dur + 0.02)
    osc.onended = () => {
      osc.disconnect()
      g.disconnect()
    }
  }

  /** Juicy "jelly squirt" on capture: a wet squeeze (bandpass noise sweeping down),
   * a bursting pop (sine bending up then back), and a tiny reward sparkle — so eating a
   * jellyfish + grabbing the jelly reads as squish → pop → ding. */
  private squirt(): void {
    if (!this.ctx) return
    const ctx = this.ctx
    const t0 = ctx.currentTime

    // Layer A — wet squeeze: bandpass noise burst whose pitch sweeps DOWN (the squish).
    const dur = 0.26
    if (!this.squirtNoise) {
      const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate)
      const data = buffer.getChannelData(0)
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
      this.squirtNoise = buffer
    }
    const src = ctx.createBufferSource()
    src.buffer = this.squirtNoise
    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.setValueAtTime(1900, t0)
    bp.frequency.exponentialRampToValueAtTime(260, t0 + dur)
    bp.Q.value = 7
    const ng = ctx.createGain()
    ng.gain.setValueAtTime(0.0001, t0)
    ng.gain.linearRampToValueAtTime(0.4, t0 + 0.015)
    ng.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    src.connect(bp)
    bp.connect(ng)
    ng.connect(this.sfxBus)
    src.start(t0)
    src.stop(t0 + dur)
    src.onended = () => {
      src.disconnect()
      bp.disconnect()
      ng.disconnect()
    }

    // Layer B — bursting pop: a sine that bends UP fast then snaps back (the jelly popping).
    const pop = ctx.createOscillator()
    const pg = ctx.createGain()
    pop.type = 'sine'
    pop.frequency.setValueAtTime(180, t0)
    pop.frequency.exponentialRampToValueAtTime(720, t0 + 0.07)
    pop.frequency.exponentialRampToValueAtTime(220, t0 + 0.16)
    pg.gain.setValueAtTime(0.0001, t0)
    pg.gain.linearRampToValueAtTime(0.3, t0 + 0.012)
    pg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18)
    pop.connect(pg)
    pg.connect(this.sfxBus)
    pop.start(t0)
    pop.stop(t0 + 0.2)
    pop.onended = () => {
      pop.disconnect()
      pg.disconnect()
    }

    // Layer C — reward sparkle: two quick upward triangle blips (got the jelly!).
    this.blip(740, 0.1, 'triangle', 0.16, 0.12)
    this.blip(1110, 0.12, 'triangle', 0.12, 0.17)
  }

  // --------------------------------------------------------------------------
  // Ambient pad
  // --------------------------------------------------------------------------
  startAmbient(): void {
    if (!this.ctx || this.ambientNodes.length) return
    const ctx = this.ctx

    // Gentle, NON-TONAL underwater wash from looped brown noise — no steady
    // oscillator drone (that read as a loud hum). Soft and slowly swelling.
    const seconds = 4
    const buffer = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate)
    const data = buffer.getChannelData(0)
    let last = 0
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1
      last = (last + 0.02 * white) / 1.02
      data[i] = last * 2.5
    }
    const noise = ctx.createBufferSource()
    noise.buffer = buffer
    noise.loop = true

    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 520
    filter.Q.value = 0.5

    const gain = ctx.createGain()
    gain.gain.value = 0.04

    noise.connect(filter)
    filter.connect(gain)
    gain.connect(this.musicBus)

    // slow filter sweep so the wash gently "breathes"
    const lfo = ctx.createOscillator()
    const lfoGain = ctx.createGain()
    lfo.frequency.value = 0.05
    lfoGain.gain.value = 200
    lfo.connect(lfoGain)
    lfoGain.connect(filter.frequency)
    lfo.start()

    // slow amplitude swell
    const ampLfo = ctx.createOscillator()
    const ampLfoGain = ctx.createGain()
    ampLfo.frequency.value = 0.06
    ampLfoGain.gain.value = 0.02
    ampLfo.connect(ampLfoGain)
    ampLfoGain.connect(gain.gain)
    ampLfo.start()

    noise.start()
    this.ambientNodes = [noise, filter, gain, lfo, lfoGain, ampLfo, ampLfoGain]

    // occasional soft bubble blips for life
    const scheduleBubble = () => {
      if (!this.ambientNodes.length) return
      this.blip(620 + Math.random() * 480, 0.07, 'sine', 0.035)
      // Re-check after blip(): if stopAmbient() ran in between it cleared ambientTimer,
      // so don't re-arm a timer it can no longer cancel (would leak one extra bubble).
      if (!this.ambientNodes.length) return
      this.ambientTimer = window.setTimeout(scheduleBubble, 2800 + Math.random() * 4500)
    }
    this.ambientTimer = window.setTimeout(scheduleBubble, 3200)
  }

  stopAmbient(): void {
    if (this.ambientTimer != null) {
      clearTimeout(this.ambientTimer)
      this.ambientTimer = null
    }
    for (const n of this.ambientNodes) {
      try {
        if ('stop' in n && typeof (n as OscillatorNode).stop === 'function') (n as OscillatorNode).stop()
      } catch {
        /* already stopped */
      }
      n.disconnect()
    }
    this.ambientNodes = []
  }
}
